import React from 'react';
import { formatCurrency } from '../../../utils/currency';
import { formatLocalizedNumber, formatLocalizedPercent } from '../../../utils/numberFormatting';
import { CostPartialMark, ReportFigure, ReportSection, reportTextOf } from '../ReportTab';
import type { ObjectCardTone } from '../../../components/layout/ObjectCard';

// Presentational primitives of the "Pulpit zarządu" views. Tiles and cards are
// the report primitives (`ReportFigure`, `ReportSection`, PR2 2026-10-29);
// metric rows, the cumulative line chart, donut and bars are drawn here. Colours come from
// the `--mgmt-*` tokens in App.css (both themes); no hex values here.

export const SERIES_COLORS = [
  'var(--mgmt-series-1)',
  'var(--mgmt-series-2)',
  'var(--mgmt-series-3)',
  'var(--mgmt-series-4)',
  'var(--mgmt-series-5)',
  'var(--mgmt-series-6)',
  'var(--mgmt-series-7)',
];

export function money(value: number | null | undefined): string {
  return value == null ? '—' : formatCurrency(Math.round(value), 'PLN').replace(/,00(?=\s)/, '');
}

export function percent(value: number | null | undefined, language: string): string {
  return value == null ? '—' : formatLocalizedPercent(value, language);
}

/** A short amount for the centre of a donut: „2,24 mln zł”, „645 tys. zł”. */
export function shortMoney(value: number, language: string): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${formatLocalizedNumber(value / 1_000_000, 2, language)} mln zł`;
  if (abs >= 10_000) return `${formatLocalizedNumber(value / 1_000, 0, language)} tys. zł`;
  return money(value);
}

/** "1,2 mln" / "350 tys." axis labels. */
export function compactAmount(value: number, language: string): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${formatLocalizedNumber(value / 1_000_000, abs >= 10_000_000 ? 0 : 1, language)} mln`;
  if (abs >= 1_000) return `${formatLocalizedNumber(value / 1_000, 0, language)} tys.`;
  return formatLocalizedNumber(value, 0, language);
}

/** A section of a view: the report section card (icon tile, 15 px title, one-line lead, actions right). */
export const DashCard: React.FC<{
  title: React.ReactNode;
  icon?: React.ReactNode;
  tone?: ObjectCardTone;
  lead?: React.ReactNode;
  extra?: React.ReactNode;
  className?: string;
  testId?: string;
  children: React.ReactNode;
}> = ({
  title, icon, tone, lead, extra, className, testId, children,
}) => (
  <ReportSection title={title} icon={icon} tone={tone} lead={lead} actions={extra} className={`mgmt-card ${className ?? ''}`}>
    {testId ? <div className="mgmt-card__body" data-testid={testId}>{children}</div> : <div className="mgmt-card__body">{children}</div>}
  </ReportSection>
);

export interface KpiProps {
  label: string;
  value: string;
  sub?: React.ReactNode;
  /** Full text of `sub` when it is longer than one line (title). */
  subTitle?: string;
  icon: React.ReactNode;
  iconTone?: ObjectCardTone;
  tone?: 'positive' | 'negative';
  marker?: boolean;
  /** What the „*” means (title). */
  markerHint?: string;
  testId?: string;
  /** The tile opens "Skąd ta liczba". */
  onClick?: () => void;
  selected?: boolean;
}

/** A figure of a dashboard view: `ReportFigure` (icon tile, 12 px label, 20 px value, one muted line). */
export const KpiTile: React.FC<KpiProps> = ({
  label, value, sub, subTitle, icon, iconTone = 'blue', tone, marker, markerHint, testId, onClick, selected,
}) => (
  <ReportFigure
    icon={icon}
    tone={iconTone}
    label={label}
    value={<>{value}{marker && <CostPartialMark hint={markerHint || '*'} testId="mgmt-kpi-partial" />}</>}
    valueClassName={tone ? `is-${tone}` : undefined}
    note={sub}
    noteTitle={subTitle}
    testId={testId}
    onClick={onClick}
    selected={selected}
  />
);

export const MetricRows: React.FC<{ rows: { label: React.ReactNode; value: React.ReactNode; tone?: 'positive' | 'negative' | 'strong'; title?: string }[] }> = ({ rows }) => (
  <dl className="mgmt-metrics">
    {rows.map((row, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <div className={`mgmt-metrics__row ${row.tone ? `is-${row.tone}` : ''}`} key={index}>
        <dt title={row.title ?? reportTextOf(row.label)}>{row.label}</dt>
        <dd>{row.value}</dd>
      </div>
    ))}
  </dl>
);

/** One muted line under a chart or table; the full text stays in `title`. */
export const Callout: React.FC<{ children: React.ReactNode; title?: string }> = ({ children, title }) => (
  <p className="mgmt-callout" title={title ?? reportTextOf(children)}>{children}</p>
);

export interface LineSeries {
  key: string;
  name: string;
  color: string;
  values: (number | null)[];
  dashed?: boolean;
}

export const LineChart: React.FC<{
  series: LineSeries[];
  labels: string[];
  language: string;
  ariaLabel: string;
  /** Month indexes of the page's period (a quarter or a month): a light band behind them. */
  highlight?: number[];
}> = ({
  series, labels, language, ariaLabel, highlight,
}) => {
  const W = 680;
  const H = 240;
  const left = 62;
  // Room for half of the last month label („gru 26”) centred on the last point.
  const right = 28;
  const top = 14;
  const bottom = 28;
  const plotW = W - left - right;
  const plotH = H - top - bottom;
  const all = series.flatMap((item) => item.values).filter((value): value is number => value != null);
  // An empty year still gets a readable axis (0 … 1 000), not „0 0 1 1 1”.
  const rawMax = Math.max(1000, ...all);
  const rawMin = Math.min(0, ...all);
  const step = niceStep((rawMax - rawMin) / 4);
  const max = Math.ceil(rawMax / step) * step;
  const min = Math.floor(rawMin / step) * step;
  const ticks: number[] = [];
  for (let value = min; value <= max + step / 2; value += step) ticks.push(value);
  const x = (index: number) => left + (labels.length <= 1 ? 0 : (index * plotW) / (labels.length - 1));
  const y = (value: number) => top + plotH - ((value - min) / (max - min || 1)) * plotH;
  const visible = series.filter((item) => item.values.some((value) => value != null));

  return (
    <div className="mgmt-chart">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={ariaLabel} preserveAspectRatio="xMidYMid meet">
        {highlight && highlight.length > 0 && (() => {
          const half = labels.length <= 1 ? plotW / 2 : plotW / (labels.length - 1) / 2;
          const first = Math.max(left, x(Math.min(...highlight)) - half);
          const last = Math.min(W - right, x(Math.max(...highlight)) + half);
          return <rect className="mgmt-chart__period" x={first} y={top} width={Math.max(4, last - first)} height={plotH} rx={4} />;
        })()}
        {ticks.map((tick) => (
          <g key={tick}>
            <line x1={left} x2={W - right} y1={y(tick)} y2={y(tick)} className={tick === 0 ? 'mgmt-chart__zero' : 'mgmt-chart__grid'} />
            <text x={left - 8} y={y(tick) + 4} textAnchor="end" className="mgmt-chart__axis">{compactAmount(tick, language)}</text>
          </g>
        ))}
        {visible.map((item) => {
          const points = item.values
            .map((value, index) => (value == null ? null : `${x(index)},${y(value)}`))
            .filter(Boolean)
            .join(' ');
          const last = item.values.reduce<number>((found, value, index) => (value == null ? found : index), -1);
          return (
            <g key={item.key}>
              <polyline
                points={points}
                fill="none"
                stroke={item.color}
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray={item.dashed ? '7 5' : undefined}
              />
              {last >= 0 && item.values[last] != null && (
                <circle cx={x(last)} cy={y(item.values[last] as number)} r={3.5} fill={item.color}>
                  <title>{`${item.name}: ${money(item.values[last])}`}</title>
                </circle>
              )}
            </g>
          );
        })}
        {labels.map((label, index) => (
          <text key={label} x={x(index)} y={H - 8} textAnchor="middle" className={`mgmt-chart__axis${highlight && highlight.length && highlight.includes(index) ? ' is-period' : ''}`}>{label}</text>
        ))}
      </svg>
      <div className="mgmt-legend">
        {visible.map((item) => (
          <span key={item.key}>
            <i className={`mgmt-legend__swatch ${item.dashed ? 'is-dashed' : ''}`} style={{ background: item.dashed ? undefined : item.color, borderColor: item.color }} />
            {item.name}
          </span>
        ))}
      </div>
    </div>
  );
};

function niceStep(raw: number): number {
  if (raw <= 0) return 1;
  const exponent = 10 ** Math.floor(Math.log10(raw));
  const fraction = raw / exponent;
  const nice = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 2.5 ? 2.5 : fraction <= 5 ? 5 : 10;
  return nice * exponent;
}

export interface DonutItem {
  key: string;
  label: string;
  value: number;
  color?: string;
  display?: string;
}

/**
 * Donut with its legend. Items of 0 are left out of the legend (nothing to
 * show); `center` names the whole (an amount or a count), never „100%”.
 */
export const Donut: React.FC<{ items: DonutItem[]; center: string; centerCaption: string; language: string; emptyText: string }> = ({
  items, center, centerCaption, language, emptyText,
}) => {
  const colored = items
    .map((item, index) => ({ ...item, color: item.color ?? SERIES_COLORS[index % SERIES_COLORS.length] }))
    .filter((item) => Math.abs(item.value) > 0.5);
  const sum = colored.reduce((total, item) => total + Math.max(0, item.value), 0);
  if (sum <= 0) return <p className="mgmt-empty">{emptyText}</p>;
  let start = 0;
  const stops = colored.filter((item) => item.value > 0).map((item) => {
    const share = (100 * item.value) / sum;
    const stop = `${item.color} ${start}% ${start + share}%`;
    start += share;
    return stop;
  });
  return (
    <div className="mgmt-donut-wrap">
      <div className="mgmt-donut" style={{ background: `conic-gradient(${stops.join(',')})` }} aria-hidden="true">
        <div className="mgmt-donut__mid">
          <strong>{center}</strong>
          <small>{centerCaption}</small>
        </div>
      </div>
      <ul className="mgmt-donut-list">
        {colored.map((item) => (
          <li key={item.key}>
            <span title={item.label}>
              <i className="mgmt-dot" style={{ background: item.color }} />
              {item.label}
            </span>
            <b>{item.display ?? percent((100 * Math.max(0, item.value)) / sum, language)}</b>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const Bars: React.FC<{ items: { key: string; label: string; value: number; display?: string }[]; emptyText: string }> = ({ items, emptyText }) => {
  const max = Math.max(0, ...items.map((item) => item.value));
  if (max <= 0) return <p className="mgmt-empty">{emptyText}</p>;
  return (
    <div className="mgmt-bars">
      {items.map((item) => (
        <div className="mgmt-bars__row" key={item.key}>
          <span className="mgmt-bars__label">{item.label}</span>
          <div className="mgmt-bars__track"><i style={{ width: `${(100 * Math.max(0, item.value)) / max}%` }} /></div>
          <b>{item.display ?? money(item.value)}</b>
        </div>
      ))}
    </div>
  );
};
